import React, { useState } from "react";
import { Layout, Menu, Typography, Card, Input, Anchor } from "antd";
import {
  FileTextOutlined,
  SafetyOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import vclogo from "../../assets/images/newLogo.png";

const { Header, Sider, Content } = Layout;
const { Title, Paragraph } = Typography;

const documents = {
  privacy: {
    title: "Privacy Policy for Verifiable Contract",
    effective: "Effective Date: 15-May-2023",
    lastUpdated: "Last Updated: 31-July-2025",
    sections: [
      {
        id: "intro",
        title: "Introduction",
        content: `Verifiable Contract (“we”, “our”, or “us”) is committed to protecting your privacy and ensuring that your personal data is handled in a safe and responsible manner. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services, in compliance with applicable data protection laws, including the General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), and other relevant privacy laws.

By using Verifiable Contract, you agree to the practices described in this Privacy Policy.`
      },
      {
        id: "info",
        title: "1. Information We Collect",
        content: `We collect personal information and non-personal data in order to provide our services efficiently and securely.

1.1 Personal Information

The types of personal data we collect may include, but are not limited to:

- Name, email address, phone number, and job title
- Company information (name, address, domain, etc.)
- Contract documents and metadata (contract name, clauses, expiration dates, etc.)
- Digital signatures and identity verification information (via DocuSign or similar providers)
- User credentials and authentication information
- Payment information (if applicable through third-party processors)
- Communication data (support requests, email correspondence)
- Usage and device data such as IP address, browser type, operating system, and activity logs

1.2 Non-Personal Information

We may collect aggregated or anonymous data, such as:

- Interaction patterns, usage statistics, and performance metrics
- Cookies and tracking technologies
- Geolocation data, if you choose to share it`
      },
      {
        id: "use",
        title: "2. How We Use Your Data",
        content: `We use the collected data to:

- Provide, maintain, and improve our services
- Authenticate users and ensure secure access
- Enable document creation, storage, and signing
- Provide personalized user experience and notifications
- Analyze trends and usage to enhance the platform
- Comply with legal obligations
- Communicate with you regarding updates, support, or promotional information (with consent where required)
- Detect and prevent fraudulent or malicious activity`
      },
      {
        id: "gdpr",
        title: "3. Legal Basis for Processing (GDPR Compliance)",
        content: `We process your personal information based on the following lawful grounds:

- Contractual necessity: To perform and deliver services you request
- Consent: Where you have explicitly agreed to receive certain communications
- Legal obligation: To comply with applicable laws and regulations
- Legitimate interest: To enhance user experience, security, and operational efficiency`
      },
      {
        id: "rights",
        title: "4. Your Rights Under Privacy Laws",
        content: `4.1 GDPR Rights

If you are located in the European Economic Area, you have the following rights:

- Right to access: Request a copy of the personal data we hold about you
- Right to rectification: Correct inaccurate or incomplete data
- Right to erasure: Request deletion of your personal data under certain conditions
- Right to restrict processing: Limit how we use your data
- Right to data portability: Obtain and reuse your data across other services
- Right to object: Object to certain processing activities, including marketing
- Right to lodge a complaint: Contact data protection authorities if you believe your rights are violated

4.2 CCPA Rights

If you are a California resident, you have the right to:

- Request information about categories and sources of personal data collected
- Request deletion of your personal data
- Opt out of the sale of your personal data (we do not sell personal data)
- Access and correct personal data`
      },
      {
        id: "sharing",
        title: "5. Sharing and Disclosure of Data",
        content: `We do not sell your personal data. However, we may share your data in the following circumstances:

- With service providers: Such as cloud providers, analytics services, payment processors, and communication tools
- With signing partners: DocuSign or other identity verification services
- For compliance: In response to legal requests, subpoenas, or government orders
- Business transfers: In the event of a merger, acquisition, or asset sale

All third parties are bound by confidentiality and data protection agreements.`
      },
      {
        id: "retention",
        title: "6. Data Retention",
        content: `We retain your personal information for as long as necessary to provide services, fulfill contractual obligations, comply with legal requirements, and resolve disputes.

Users can request deletion, and we will retain only anonymized or aggregated data where possible.`
      },
      {
        id: "security",
        title: "7. Security Measures",
        content: `We use industry-standard encryption (SSL/TLS), access controls, secure storage, and periodic audits to safeguard your data. However, no method is 100% secure, and we recommend users protect their credentials and access points.`
      },
      {
        id: "cookies",
        title: "8. Cookies and Tracking Technologies",
        content: `We use cookies to enhance functionality, track usage, and optimize user experience. You can manage cookie preferences through your browser settings.

For more details, please refer to our Cookie Policy [link].`
      },
      {
        id: "intl",
        title: "9. International Data Transfers",
        content: `Your information may be processed or stored in countries outside your jurisdiction, including the United States or European Union. We ensure appropriate safeguards, such as Standard Contractual Clauses, are in place to protect your rights.`
      },
      {
        id: "children",
        title: "10. Children's Privacy",
        content: `Our services are not directed toward children under the age of 16 (or applicable age in your jurisdiction). We do not knowingly collect personal data from minors.`
      },
      {
        id: "changes",
        title: "11. Changes to This Policy",
        content: `We may update this Privacy Policy to reflect changes in our practices or legal requirements. We will notify you of material changes via email or platform notifications.`
      },
      {
        id: "contact",
        title: "12. Contact Us",
        content: `If you have any questions or concerns about this Privacy Policy or how your data is handled, please contact us at:

Email: info@verifiablecontract.com
Address: USA`
      }
    ]
  },
  terms: {
    title: "Terms of Service",
    effective: "Effective Date: 15-May-2023",
    lastUpdated: "Last Updated: 31-July-2025",
    sections: [
      {
        id: "acceptance",
        title: "1. Acceptance of Terms",
        content: `These Terms govern your access to and use of the Service, including our website, applications, application plug-ins, and other services provided by us. Website visitors are referred to as "Site Visitors," and individuals or entities who use the Service or create an account ("Account") are collectively referred to as "Customers."

If you are a paid Customer ("Paid Customer") and are bound by an Enterprise Services Agreement with Verifiable Contract ("Enterprise Terms"), then these Terms will apply only to the extent your use of the Service is not already governed by those Enterprise Terms.`
      },
      {
        id: "service",
        title: "2. Description of Service",
        content: `Verifiable Contract provides a Contract Lifecycle Management (CLM) platform that enables users to:

- Create, edit, and manage contracts
- Store, share, and track contract versions
- Collaborate with other users and parties
- Use integrations such as DocuSign for digital signatures
- Access analytics and reporting tools related to contracts

We reserve the right to modify, suspend, or discontinue any features without prior notice.`
      },
      {
        id: "accounts",
        title: "3. User Accounts",
        content: `To access certain features, you must create a user account. You are responsible for:

- Maintaining the security and confidentiality of your login credentials
- All activities conducted through your account
- Promptly informing us of any unauthorized access or suspicious activity

We reserve the right to suspend or terminate accounts that violate these Terms or pose a security risk.`
      },
      {
        id: "responsibilities",
        title: "4. User Responsibilities",
        content: `You agree not to:

- Use the platform for unlawful, fraudulent, or malicious purposes
- Violate intellectual property rights or share content without proper authorization
- Interfere with or disrupt the platform’s operation or servers
- Attempt to gain unauthorized access to other users’ data or accounts

You are responsible for ensuring that all data uploaded and shared complies with applicable laws and that necessary permissions are obtained from involved parties.`
      },
      {
        id: "ip",
        title: "5. Intellectual Property",
        content: `All content, software, logos, trademarks, and platform design are owned by or licensed to Verifiable Contract.

You retain ownership of the content you upload, but you grant us a limited license to operate, store, and process your content for providing services.

You may not copy, distribute, or reproduce platform content without explicit written permission.`
      },
      {
        id: "thirdparty",
        title: "6. Third-Party Services",
        content: `Our platform integrates with third-party providers, including but not limited to:

- DocuSign or other e-signature services
- Cloud hosting providers
- Analytics and communication services

These services have their own terms and privacy policies. We are not responsible for their actions or services but will ensure that data sharing is done under appropriate agreements and safeguards.`
      },
      {
        id: "payment",
        title: "7. Payment Terms (If Applicable)",
        content: `If you subscribe to premium features or paid plans, you agree to:

- Provide accurate billing information
- Pay all fees as specified during the subscription process
- Accept applicable taxes, payment schedules, and refund policies

We reserve the right to suspend access if payments are not received as agreed.`
      },
      {
        id: "liability",
        title: "8. Limitation of Liability",
        content: `To the maximum extent permitted by law, Verifiable Contract and its affiliates are not liable for:

- Indirect, incidental, or consequential damages arising from your use of the platform
- Loss of data, profits, or goodwill due to technical issues or unauthorized access
- Disputes between users or between users and third parties

Our total liability for any claim arising from the platform is limited to the amount you paid to us in the 12 months preceding the event.`
      },
      {
        id: "warranty",
        title: "9. Disclaimer of Warranties",
        content: `We provide our platform “as is” and “as available” without warranties, expressed or implied. We do not guarantee uninterrupted service, error-free operation, or accuracy of information.

Users are responsible for verifying the content of contracts and using appropriate legal counsel when necessary.`
      },
      {
        id: "privacy",
        title: "10. Data Protection and Privacy",
        content: `Your use of our services is subject to our Privacy Policy, which explains how your data is collected, used, and shared. By using the platform, you consent to such practices as outlined in that policy.`
      },
      {
        id: "termination",
        title: "11. Termination",
        content: `We may suspend or terminate your account and access if:

- You violate these Terms
- You engage in fraudulent or illegal activities
- You repeatedly misuse the platform
- We are required to comply with legal or regulatory requirements

You may also close your account at any time by contacting support.`
      },
      {
        id: "law",
        title: "12. Governing Law and Dispute Resolution",
        content: `These Terms are governed by the laws of [Insert Jurisdiction, e.g., State of California, United States], without regard to conflict of laws.

Any disputes arising from these Terms will be resolved through binding arbitration or mediation before pursuing legal action.`
      },
      {
        id: "contact",
        title: "13. Contact Information",
        content: `For questions or concerns regarding these Terms, please contact us at:

Email: info@verifiablecontract.com
Address: USA`
      },
      {
        id: "misc",
        title: "14. Miscellaneous",
        content: `These Terms, along with the Privacy Policy, form the entire agreement between you and Verifiable Contract.

If any provision is found invalid, the remaining provisions will continue in full force.

We reserve the right to update or amend these Terms as necessary to reflect changes in services or legal requirements.`
      }
    ]
  }
};

// Same LegalCenter component code (unchanged from before)
const LegalCenter = () => {
  const [activeDoc, setActiveDoc] = useState("privacy");
  const doc = documents[activeDoc];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Header
        style={{
          background: "#fff",
          padding: "0 24px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "9vh",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <img
            src={vclogo}
            alt="Verifiable Logo"
            style={{ height: "48px", marginLeft: "20px" }}
          />
          <Title level={4} style={{ margin: 0 }}>
            {/* Legal Center */}
          </Title>
        </div>
      </Header>

      <Layout style={{ height: "91vh" }}>
        <Sider
          theme="light"
          width={260}
          style={{ borderRight: "1px solid #f0f0f0", paddingTop: "20px" }}
        >
          <Menu
            mode="inline"
            defaultSelectedKeys={["privacy"]}
            onClick={(e) => setActiveDoc(e.key)}
            items={[
              { key: "privacy", icon: <SafetyOutlined />, label: "Privacy Policy" },
              { key: "terms", icon: <FileTextOutlined />, label: "Terms of Service" },
            ]}
          />
        </Sider>

        <Content style={{ padding: "24px 48px", textAlign: "left" }}>
          <Card
            bordered={false}
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              height: "85vh",
              overflowY: "auto",
            }}
          >
            <Title level={2}>{doc.title}</Title>
            <Paragraph type="secondary">{doc.effective}</Paragraph>
            <Paragraph strong>{doc.lastUpdated}</Paragraph>

            <div style={{ marginBottom: 20, display: "flex", gap: "12px" }}>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search in document..."
                style={{ width: 250 }}
              />
            </div>

            <Anchor affix={false} style={{ marginBottom: 20 }}>
              {doc.sections.map((sec) => (
                <Anchor.Link key={sec.id} href={`#${sec.id}`} title={sec.title} />
              ))}
            </Anchor>

            {doc.sections.map((sec) => (
              <div key={sec.id} id={sec.id} style={{ marginBottom: "32px" }}>
                <Title level={4}>{sec.title}</Title>
                <Paragraph style={{ whiteSpace: "pre-line" }}>
                  {sec.content}
                </Paragraph>
              </div>
            ))}
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default LegalCenter;
